import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Loader2, Sparkles, ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';
import { useAuth } from '@/hooks/useAuth';
import { IneaMark } from '@/components/IneaMark';
import { ApiError } from '@/lib/api';
import i18n, { SUPPORTED_LANGS, ensureLangLoaded, type LangCode } from '@/i18n';
import { useFeatures } from '@/hooks/useFeatures';

export default function LoginPage() {
  const { t } = useTranslation();
  const { login, register } = useAuth();
  const navigate = useNavigate();
  // Lot A (2026-07-20) — inscriptions directes fermées (exclusif membres
  // Mars) : le mode register est masqué et le défaut passe à 'login'.
  const { direct_signup_enabled: signupEnabled } = useFeatures();
  const [mode, setMode] = useState<'login' | 'register'>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  /** Preferred language picked at signup. Default = currently active UI lang. */
  const [preferredLang, setPreferredLang] = useState<LangCode>(
    (i18n.resolvedLanguage as LangCode) ?? 'fr',
  );
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  async function onSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      if (mode === 'login') {
        await login(email, password);
      } else {
        // Lock UI to the chosen lang BEFORE register so the X-Lang header
        // sent by api.ts is the right one (drives all future generations).
        if (preferredLang !== i18n.resolvedLanguage) {
          await ensureLangLoaded(preferredLang);
          await i18n.changeLanguage(preferredLang);
        }
        await register(email, password, displayName || undefined, preferredLang);
      }
      navigate('/');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.errorGeneric'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="grid min-h-screen lg:grid-cols-[1.05fr_1fr]">
      {/* ── Left — immersive brand panel (desktop only) ──────────────── */}
      <aside className="relative hidden overflow-hidden lg:flex lg:flex-col lg:justify-between lg:p-12">
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              'radial-gradient(ellipse 70% 60% at 25% 20%, rgba(59,130,246,0.16), transparent 65%), radial-gradient(ellipse 60% 55% at 80% 85%, rgba(236,72,153,0.16), transparent 62%)',
          }}
        />
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <span className="kicker text-pink">Network-iA</span>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.12, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="relative"
        >
          <div className="mb-7 flex items-center gap-4">
            <div className="animate-float">
              <IneaMark size={80} />
            </div>
            <div>
              <p className="font-mono text-[11px] font-700 uppercase tracking-[0.2em] text-ink/45">
                {t('login.coachKicker')}
              </p>
              <p className="font-display text-2xl font-700">Inea</p>
            </div>
          </div>
          <h2 className="font-display text-[clamp(28px,3.4vw,42px)] font-700 leading-[1.1] tracking-[-0.03em]">
            {t('login.tagline1')}{' '}
            <span className="text-grad">{t("login.tagline2")}</span>.
          </h2>
          <p className="mt-4 max-w-md text-[15px] leading-[1.65] text-ink/60">
            {t('login.ineaBlurb')}
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3, duration: 0.6 }}
          className="relative flex gap-2.5"
        >
          {[t('login.feature1'), t('login.feature2'), t('login.feature3')].map((f) => (
            <span
              key={f}
              className="rounded-full border border-ink/[0.10] bg-white/60 px-3.5 py-1.5 text-[12px] font-600 text-ink/65 backdrop-blur"
            >
              {f}
            </span>
          ))}
        </motion.div>
      </aside>

      {/* ── Right — auth form ────────────────────────────────────────── */}
      <div className="flex items-center justify-center px-5 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="card w-full max-w-md p-8 sm:p-10"
        >
          <div className="mb-6 flex flex-col items-center text-center lg:hidden">
            <IneaMark size={56} />
          </div>
          <h1 className="font-display text-[26px] font-700 tracking-[-0.025em]">
            {mode === 'login' ? t('login.welcomeBack') : t('login.registerTitle')}
          </h1>
          <p className="mt-1.5 text-[14px] text-ink/55">
            {mode === 'login'
              ? t('login.loginSubtitle')
              : t('login.registerSubtitle')}
          </p>

          {/* Tabs — register masqué quand les inscriptions directes sont fermées */}
          {!signupEnabled && (
            <p className="mt-4 rounded-xl border border-ink/[0.08] bg-ink/[0.03] px-3 py-2.5 text-[12.5px] text-ink/60">
              {t('login.marsOnly')}
            </p>
          )}
          <div className="mt-6 flex rounded-2xl border border-ink/[0.08] bg-ink/[0.03] p-1">
            {(signupEnabled ? (['register', 'login'] as const) : (['login'] as const)).map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => {
                  setMode(m);
                  setError('');
                }}
                className="relative flex-1 rounded-xl py-2.5 text-[13px] font-700 transition"
              >
                {mode === m && (
                  <motion.span
                    layoutId="auth-tab"
                    className="absolute inset-0 rounded-xl"
                    style={{ background: 'linear-gradient(135deg, #3b82f6, #ec4899)' }}
                    transition={{ type: 'spring', stiffness: 380, damping: 30 }}
                  />
                )}
                <span className={mode === m ? 'relative text-white' : 'relative text-ink/55'}>
                  {m === "register" ? t("login.tabRegister") : t("login.tabLogin")}
                </span>
              </button>
            ))}
          </div>

          <form onSubmit={onSubmit} className="mt-6 space-y-4">
            {mode === 'register' && (
              <Field
                label="Prénom"
                value={displayName}
                onChange={setDisplayName}
                type="text"
                placeholder={t("login.firstNamePlaceholder")}
              />
            )}
            <Field
              label="Email"
              value={email}
              onChange={setEmail}
              type="email"
              placeholder={t("login.emailPlaceholder")}
              required
            />
            <Field
              label="Mot de passe"
              value={password}
              onChange={setPassword}
              type="password"
              placeholder={t("login.passwordPlaceholder")}
              required
            />

            {mode === 'register' && (
              <div>
                <label className="mb-1.5 block text-[12px] font-700 uppercase tracking-[0.1em] text-ink/55">
                  {t('common.language')}
                </label>
                <select
                  value={preferredLang}
                  onChange={(e) => setPreferredLang(e.target.value as LangCode)}
                  className="w-full rounded-2xl border border-ink/[0.12] bg-white px-3.5 py-3 text-[14px] font-600 text-ink outline-none transition focus:border-pink/60 focus:ring-2 focus:ring-pink/20"
                >
                  {SUPPORTED_LANGS.map((l) => (
                    <option key={l.code} value={l.code}>
                      {l.flag}  {l.label}
                    </option>
                  ))}
                </select>
                <p className="mt-1.5 text-[11.5px] text-ink/55">
                  {t('login.languageHelp', 'Inea générera tous vos contenus dans cette langue.')}
                </p>
              </div>
            )}

            {error && (
              <motion.p
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                className="rounded-xl bg-pink/10 px-3.5 py-2.5 text-[13px] font-500 text-pink-deep"
              >
                {error}
              </motion.p>
            )}

            <button type="submit" disabled={busy} className="btn-primary w-full disabled:opacity-60">
              {busy ? (
                <Loader2 size={16} className="animate-spin" />
              ) : mode === 'login' ? (
                <ArrowRight size={16} />
              ) : (
                <Sparkles size={16} />
              )}
              {mode === "login" ? t("login.loginCta") : t("login.registerCta")}
            </button>
          </form>
        </motion.div>
      </div>
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  type,
  placeholder,
  required,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type: string;
  placeholder?: string;
  required?: boolean;
}) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-[12px] font-600 text-ink/70">
        {label} {required && <span className="text-pink">*</span>}
      </span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        required={required}
        className="w-full rounded-2xl border border-ink/[0.12] bg-white px-4 py-3 text-[14px]
                   text-ink outline-none transition focus:border-pink/60 focus:ring-2 focus:ring-pink/20"
      />
    </label>
  );
}
